import React, { useState, useEffect } from 'react';
import { getActivities } from '../services/firebase';
import VideoModal from './VideoModal';
import Header from './Header';
import { ActivityList } from './ActivityList';
import { SelectedActivities } from './SelectedActivities';
import { useAuth } from '../contexts/AuthContext';
import { PDFDownloadLink } from '@react-pdf/renderer';
import SessionPlanPDF from './SessionPlanPDF';

const SessionPlanner = () => {
  console.log('SessionPlanner rendering...');
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('SessionPlanner: Loading activities...');
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching activities from Firebase...');
      const activitiesData = await getActivities();
      console.log('Activities loaded:', activitiesData);
      
      if (!Array.isArray(activitiesData)) {
        throw new Error('Invalid activities data received');
      }
      
      setActivities(activitiesData);
    } catch (error) {
      console.error('Error loading activities:', error);
      setError(error.message || 'Failed to load activities');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter out activities that have matching skills with selected activities
  const filteredActivities = activities.filter(activity => {
    if (selectedActivities.length === 0) return true;
    
    const activitySkills = new Set(
      (typeof activity.skillTags === 'string' 
        ? activity.skillTags.split(',') 
        : activity.skillTags || []
      ).map(skill => skill.trim().toLowerCase())
    );

    const selectedSkills = new Set(
      selectedActivities.flatMap(selected => 
        (typeof selected.skillTags === 'string'
          ? selected.skillTags.split(',')
          : selected.skillTags || []
        ).map(skill => skill.trim().toLowerCase())
      )
    );

    // Return false if there's any skill overlap
    return ![...activitySkills].some(skill => selectedSkills.has(skill));
  });

  const handleAddActivity = (activity) => {
    console.log('Adding activity:', activity);
    if (!selectedActivities.some((a) => a.name === activity.name)) {
      setSelectedActivities([...selectedActivities, activity]);
    }
  };

  const handleRemoveActivity = (activityName) => {
    console.log('Removing activity:', activityName);
    setSelectedActivities(
      selectedActivities.filter((activity) => activity.name !== activityName)
    );
  };

  console.log('SessionPlanner state:', {
    activitiesCount: activities.length,
    selectedActivitiesCount: selectedActivities.length,
    loading,
    error,
    user
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading activities...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="text-red-600 text-xl mb-4">Error loading activities</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadActivities}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold mb-6">Activity Library</h2>
            <ActivityList
              activities={filteredActivities}
              onAddActivity={handleAddActivity}
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Selected Activities</h2>
              {selectedActivities.length >= 3 && (
                <PDFDownloadLink
                  document={<SessionPlanPDF activities={selectedActivities} />}
                  fileName={`session-plan-${new Date().toISOString().split('T')[0]}.pdf`}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
                >
                  {({ blob, url, loading, error }) =>
                    loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Generating PDF...</span>
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586L7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                        </svg>
                        <span>Download PDF</span>
                      </>
                    )
                  }
                </PDFDownloadLink>
              )}
            </div>
            <SelectedActivities
              selectedActivities={selectedActivities}
              onRemoveActivity={handleRemoveActivity}
            />
          </div>
        </div>
      </div>
      {selectedVideo && (
        <VideoModal
          videoUrl={selectedVideo}
          onClose={() => setSelectedVideo(null)}
        />
      )}
    </div>
  );
};

export default SessionPlanner; 